import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, KeyRound, FileSignature, Eye, Send, Check, RotateCcw } from 'lucide-react';

const DEMO = {
  mode: 'internal', source_country: 'KE', source_name: 'Bullaale AS', source_account: '1100194977404',
  dest_type: 'bank', dest_country: 'KE', dest_name: 'Tom Doe', dest_account: '0020100014605',
  amount: 10.0, currency: 'KES', description: 'EzyTransfer live demo settlement',
  source_rail: 'mint', source_rail_reference: 'DEMO',
};

const newRef = () => `EZYEQDEMO${Math.random().toString(16).slice(2, 8).toUpperCase()}`;

const STEPS = [
  { n: 1, t: 'Authenticate', icon: KeyRound, d: 'Exchange the merchant Api-Key for a Jenga bearer token (POST /authentication/api/v3/authenticate/merchant).' },
  { n: 2, t: 'Sign', icon: FileSignature, d: 'Concatenate the endpoint fields in exact order (no separators) and sign with our RSA private key — SHA-256, PKCS#1 v1.5, base64. Jenga verifies with the public key registered in the portal.' },
  { n: 3, t: 'Prepare', icon: Eye, d: 'Assemble the full signed request: endpoint, Authorization + signature headers, and JSON body. Nothing is transmitted yet.' },
  { n: 4, t: 'Send', icon: Send, d: 'Transmit the signed request to the Jenga Send Money endpoint and read the live response.' },
];

const Code = ({ children, testid }) => (
  <pre className="mt-2 p-3 bg-slate-900 rounded text-[11px] font-mono text-slate-100 whitespace-pre-wrap break-all max-h-56 overflow-y-auto" data-testid={testid}>{children}</pre>
);

const EquityDemoMode = ({ env }) => {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState(newRef);
  const [results, setResults] = useState({});

  const sigString = `${DEMO.source_account}${DEMO.amount.toFixed(2)}${DEMO.currency}${ref}`;
  const payload = { ...DEMO, env, reference: ref };

  const run = async (n) => {
    setBusy(true);
    try {
      if (n === 1) {
        const r = await api.post(`/equity/token?env=${env}`);
        setResults((x) => ({ ...x, 1: { ok: true, text: `Bearer token issued (${env})\ntoken: ${r.data.token_preview}\nexpires in: ${r.data.expires_in}s${r.data.cached ? ' (cached)' : ''}` } }));
      } else if (n === 2) {
        const r = await api.post('/equity/signature', { fields: [sigString] });
        setResults((x) => ({ ...x, 2: { ok: true, text: `signature string:\n${r.data.concatenated_string}\n\nalgorithm: ${r.data.algorithm}\n\nsignature header:\n${r.data.signature}` } }));
      } else if (n === 3) {
        const r = await api.post('/equity/transfer', { ...payload, confirm: false });
        const q = r.data.request;
        setResults((x) => ({ ...x, 3: { ok: true, text: `POST ${q.endpoint}\n\nheaders:\n${JSON.stringify(q.headers, null, 2)}\n\nbody:\n${JSON.stringify(q.body, null, 2)}` } }));
      } else if (n === 4) {
        const r = await api.post('/equity/transfer', { ...payload, confirm: true });
        const res = r.data.result;
        const ok = r.data.mode === 'executed';
        setResults((x) => ({ ...x, 4: { ok, text: `reference: ${res.reference}\njenga transaction id: ${res.jenga_transaction_id || '-'}\n\nlive Jenga response:\n${JSON.stringify(res.response, null, 2)}` } }));
        if (ok) toast.success(`Settled — Jenga txn ${res.jenga_transaction_id || res.reference}`);
      }
      if (n < 4) setStep(n + 1);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Step failed';
      setResults((x) => ({ ...x, [n]: { ok: false, text: String(msg) } }));
      toast.error(String(msg).slice(0, 120));
    } finally { setBusy(false); }
  };

  const restart = () => { setStep(1); setResults({}); setRef(newRef()); };

  return (
    <Card className="p-6 border-red-300 bg-gradient-to-b from-red-50/40 to-white" data-testid="equity-demo-mode">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h4 className="text-sm font-semibold">Guided demo — how EzyTransfer settles into Equity via Jenga</h4>
        <Button variant="ghost" size="sm" onClick={restart} data-testid="equity-demo-restart-btn"><RotateCcw className="w-3 h-3 mr-1" /> Restart</Button>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Fixed walkthrough transfer: <span className="font-mono">{DEMO.source_name} ({DEMO.source_account}) → {DEMO.dest_name} ({DEMO.dest_account}) · {DEMO.amount.toFixed(2)} {DEMO.currency} · ref {ref}</span> on <b>{env.toUpperCase()}</b>
      </p>

      <div className="flex items-center mb-5" data-testid="equity-demo-stepper">
        {STEPS.map((s, i) => {
          const done = !!results[s.n]?.ok && step > s.n;
          const active = step === s.n;
          return (
            <React.Fragment key={s.n}>
              <button type="button" onClick={() => setStep(s.n)} className="flex items-center gap-1.5 shrink-0" data-testid={`equity-demo-step-${s.n}`}>
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${done ? 'bg-emerald-600 border-emerald-600 text-white' : active ? 'bg-red-700 border-red-700 text-white' : 'border-slate-300 text-slate-400'}`}>
                  {done ? <Check className="w-4 h-4" /> : s.n}
                </span>
                <span className={`text-xs font-medium ${active ? 'text-red-700' : done ? 'text-emerald-700' : 'text-slate-400'}`}>{s.t}</span>
              </button>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${step > s.n ? 'bg-emerald-400' : 'bg-slate-200'}`} />}
            </React.Fragment>
          );
        })}
      </div>

      {STEPS.filter((s) => s.n === step).map((s) => (
        <div key={s.n} data-testid={`equity-demo-panel-${s.n}`}>
          <p className="text-xs text-slate-600 mb-2">{s.d}</p>
          {s.n === 2 && (
            <p className="text-[11px] font-mono bg-amber-50 border border-amber-200 rounded p-2 mb-2" data-testid="equity-demo-sig-string">
              source.accountNumber + amount + currencyCode + reference = <b>{sigString}</b>
            </p>
          )}
          <Button onClick={() => run(s.n)} disabled={busy} className="bg-red-700 hover:bg-red-800" data-testid={`equity-demo-run-${s.n}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <s.icon className="w-4 h-4 mr-1" />}
            {s.n === 1 ? 'Get bearer token' : s.n === 2 ? 'Generate RSA signature' : s.n === 3 ? 'Build signed request' : `Send on ${env.toUpperCase()}`}
          </Button>
          {results[s.n] && (
            <>
              <Badge className={`ml-2 text-[10px] border ${results[s.n].ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`} data-testid={`equity-demo-result-badge-${s.n}`}>
                {results[s.n].ok ? 'OK' : 'REJECTED'}
              </Badge>
              <Code testid={`equity-demo-result-${s.n}`}>{results[s.n].text}</Code>
              {s.n === 4 && !results[4].ok && (
                <p className="text-[11px] text-amber-700 mt-2" data-testid="equity-demo-send-note">
                  The request was authenticated, signed and accepted at Jenga's gateway — the rejection above is Jenga's business response (e.g. merchant account linkage pending). Once Equity activates the linkage, this same step returns SUCCESS with a transactionId.
                </p>
              )}
            </>
          )}
        </div>
      ))}
    </Card>
  );
};

export default EquityDemoMode;
